'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { FeatureAccount } from './FeatureAccount'
import { FeatureAgenda } from './FeatureAgenda'
import { FeatureCalendar } from './FeatureCalendar'
import { FeatureRoutines } from './FeatureRoutines'
import { FeatureTasks } from './FeatureTasks'
import { FeatureMeals } from './FeatureMeals'
import { FeatureInspiration } from './FeatureInspiration'
import { FeatureSearch } from './FeatureSearch'
import { FeatureSettings } from './FeatureSettings'
import { ScreenshotZoomProvider } from './ScreenshotZoom'
import { SliderNav } from './SliderNav'

// Simple swipe hook for horizontal navigation
function useSwipe(
  ref: React.RefObject<HTMLElement | null>,
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
  threshold = 50
) {
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)
  const gestureDirection = useRef<'none' | 'horizontal' | 'vertical'>('none')

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX
      touchStartY.current = e.touches[0].clientY
      gestureDirection.current = 'none'
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX.current === 0) return
      const deltaX = e.touches[0].clientX - touchStartX.current
      const deltaY = e.touches[0].clientY - touchStartY.current

      if (gestureDirection.current === 'none') {
        if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
          gestureDirection.current = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
        }
      }

      if (gestureDirection.current === 'horizontal') {
        e.preventDefault()
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === 0) return
      const deltaX = e.changedTouches[0].clientX - touchStartX.current
      const wasHorizontal = gestureDirection.current === 'horizontal'

      touchStartX.current = 0
      touchStartY.current = 0
      gestureDirection.current = 'none'

      if (wasHorizontal && Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          onSwipeRight()
        } else {
          onSwipeLeft()
        }
      }
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [ref, onSwipeLeft, onSwipeRight, threshold])
}

const features = [
  { id: 'agenda', component: FeatureAgenda },
  { id: 'calendar', component: FeatureCalendar },
  { id: 'routines', component: FeatureRoutines },
  { id: 'tasks', component: FeatureTasks },
  { id: 'meals', component: FeatureMeals },
  { id: 'inspiration', component: FeatureInspiration },
  { id: 'search', component: FeatureSearch },
  { id: 'account', component: FeatureAccount },
  { id: 'settings', component: FeatureSettings },
]

interface FeaturesSliderProps {
  /** Drops the section's own heading and page padding so the slider can sit
   *  inside prose that already provides both — a blog article, for one. The
   *  slider itself, its nav and its swipe behaviour are unchanged. */
  embedded?: boolean
}

export function FeaturesSlider({ embedded = false }: FeaturesSliderProps = {}) {
  const t = useTranslations('featuresSlider')
  const [currentIndex, setCurrentIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev))
  }, [])

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < features.length - 1 ? prev + 1 : prev))
  }, [])

  // Enable swipe navigation
  useSwipe(containerRef, goToNext, goToPrevious, 80)

  const CurrentFeature = features[currentIndex].component

  return (
    <section
      ref={containerRef}
      className={embedded ? 'flex flex-col gap-[24px]' : 'py-[64px] flex flex-col gap-[24px]'}
    >
      {/* Section header with color bar. Embedded, the article's own heading is
          already doing this job, so it would be a second title for one idea. */}
      {!embedded && (
        <div className="flex ml-[36px]">
          <div className="w-[8px] shrink-0 rounded-full bg-[#6B8E6B]" />
          <div className="flex flex-col gap-[4px] pl-[12px] pr-[24px]">
            <h2 className="text-xxl font-bold">{t('headingLine1')}</h2>
            <p className="text-xxl font-light tracking-[7.8px]">{t('headingWord')}</p>
          </div>
        </div>
      )}

      {/* Content area with padding */}
      <div
        className={
          embedded
            ? 'flex flex-col gap-[24px]'
            : 'pl-[36px] pr-[36px] flex flex-col gap-[24px]'
        }
      >
        {/* Intro text */}
        <p className="text-l">
          {t('intro')}
        </p>

        {/* Dashed line */}
        <div className="border-t border-dashed border-[#999999]" />

        {/* Slider navigation bar */}
        <SliderNav
          current={currentIndex}
          total={features.length}
          onPrevious={goToPrevious}
          onNext={goToNext}
          counterLabel={t('counter', { current: currentIndex + 1, total: features.length })}
          previousLabel={t('previousLabel')}
          nextLabel={t('nextLabel')}
        />

        {/* Current feature */}
        {/* One zoom state for the whole run: open a screenshot, swipe on, and
            the next one is open too. */}
        <div className="min-h-[300px]">
          <ScreenshotZoomProvider>
            <CurrentFeature />
          </ScreenshotZoomProvider>
        </div>
      </div>
    </section>
  )
}
